import React from 'react'
import _ from 'lodash/fp'
import { connect } from 'react-redux'
import { useQuery } from 'react-apollo'
import gql from 'graphql-tag'
import { makeStyles } from '@material-ui/core/styles'

import { builder, BuilderComponent } from '@builder.io/react'

import Grid from '@material-ui/core/Grid'
import Tooltip from '@material-ui/core/Tooltip'
import { EmailShareButton, FacebookShareButton, FacebookIcon } from 'react-share'

import EmailIcon from '@material-ui/icons/Email'
import FileCopyIcon from '@material-ui/icons/FileCopy'
import ShareIcon from '@material-ui/icons/Share'
import InfoIcon from '@material-ui/icons/Info'

import ShareButton, { copyToClipboardFallback } from '../../../components/ShareButton'
import Button from '../../../components/Button'
import useShareData from './useShareData'

import * as ProfileReducer from '../../../reducers/profile'

const ticketTypeNames = {
  signup: 'tilmelding',
  union: 'DGU',
  invited: 'via vens link',
  referral: 'inviterede venner',
  verified: 'spillede runder',
  new_club: 'nye baner',
  multiplayer: 'spil med buddies'
}

const ticketsQuery = gql`
query {
  ticketsGrouped {
    type
    count
  }
}
`

builder.init('32eea804311a4d17817f2e534ded7af8')

const builders = {
  openHeader: '62ee005f460f40cfa629e0c2a253f928',
  openHeaderTickets: '7313d1a567a348ec89cc0dc2fc8d777d',
  openPartners1: '7039f8cfa35443eca3b5ae005ee355b7',
  openWeeklyPrices: 'ca00fe4541504a218af874bf911c5eb9',
  openPartners2: '163b6a27db6045bc850c7123330ed95e',
  openFooter: '4b031d54039c4ad392e05d9088901c8f',
  lastWeek: 'c310fd5b95d2401c8dfa4bd39cf3261b'
}

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    justifyContent: 'space-around',
    flexDirection: 'column',
    [theme.breakpoints.up('sm')]: {
      flexDirection: 'row'
    }
  },
  item: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'row-reverse',
    justifyContent: 'space-between',
    [theme.breakpoints.up('sm')]: {
      flexDirection: 'column'
    }
  },
  number: {
    fontSize: 16,
    [theme.breakpoints.up('sm')]: {
      fontSize: 64
    }
  },
  label: {
    display: 'flex',
    alignItems: 'center',
    fontSize: 12,
    [theme.breakpoints.up('sm')]: {
      fontSize: 16
    }
  },
  linkSection: {
    marginTop: 32,
    [theme.breakpoints.up('sm')]: {
      marginTop: 64
    }
  }
}))

const ticketName = ticket => _.flow(
  _.get('type'),
  _.getOr('andet', _.__, ticketTypeNames)
)(ticket)

const InfoIconText = props => {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', fontSize: '1rem' }}>
      {_.map.convert({ cap: false })((ticket, index) => <span key={`ticket_info_${index}`}>{_.get('count', ticket)} for {ticketName(ticket)}</span>, props.groups)}
    </div>
  )
}

const InfoIconThingy = props => {
  if (props.hidden) {
    return false
  }
  return (
    <Tooltip title={<InfoIconText groups={props.groups} />}>
      <InfoIcon style={{ fontSize: '1.5em' }} />
    </Tooltip>
  )
}

const TicketDisplay = props => {
  const classes = useStyles()
  const count = props.count || _.sumBy('count', props.groups) || 0

  const groupsWithTickets = _.flow(_.filter('count'), _.size)(props.groups)

  return (
    <div className={classes.item}>
      <span className={classes.number} style={{ ...props.total ? { fontWeight: 600 } : {} }}>{count}</span>
      <span className={classes.label}>
        {props.label} <InfoIconThingy groups={props.groups} hidden={groupsWithTickets <= 1} />
      </span>
    </div>
  )
}

const TicketRow = props => {
  const classes = useStyles()
  const tickets = _.keyBy('type', props.ticketsGrouped)

  const signupTickets = _.filter('count', [
    _.get('signup', tickets),
    _.get('invited', tickets)
  ])

  return (
    <div className={classes.container}>
      <TicketDisplay groups={signupTickets} label='Tilmelding' />
      <TicketDisplay count={_.get('referral.count', tickets)} label={ticketTypeNames.referral} />
      <TicketDisplay count={_.get('verified.count', tickets)} label='Spillede runder' />
      <TicketDisplay total count={_.sumBy('count', props.ticketsGrouped)} label='Total' />
    </div>
  )
}

const LinkSection = _props => {
  const classes = useStyles()
  const handleClick = _e => {
    if (!navigator.clipboard) {
      return copyToClipboardFallback(urlInputRef)
    }
    navigator.clipboard.writeText(shareData.url).catch(_err => copyToClipboardFallback(urlInputRef))
  }
  const shareData = useShareData()
  const urlInputRef = React.useRef()

  return (
    <Grid className={classes.linkSection} container>
      <Grid item xs={12} sm={8}>
        <BuilderComponent
          model='content-sections'
          entry='424a45b09c424e6f97afceda8c56fb07'
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <h2>Dit GLFR Open link</h2>
        <input style={{ display: 'none', width: '100%', fontSize: 16 }} readOnly value={shareData.url} ref={urlInputRef} />
        <div style={{ display: 'flex', justifyContent: 'space-evenly', alignItems: 'center', fontSize: 48 }}>
          <FacebookShareButton {...shareData}>
            <FacebookIcon size={36} round />
          </FacebookShareButton>
          <EmailShareButton {...shareData}>
            <Button fontSize='inherit' component={EmailIcon} style={{ justifyContent: 'center', alignItems: 'center', display: 'flex' }} />
          </EmailShareButton>

          <div>
            <ShareButton
              component={ShareIcon}
              fontSize='inherit'
              dialogTitle='Del dit link'
              shareData={shareData}
            >
              Del mit link
            </ShareButton>
          </div>
          <div>
            <Button style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }} component={FileCopyIcon} fontSize='inherit' onClick={handleClick}>Kopier</Button>
          </div>
        </div>
      </Grid>
    </Grid>
  )
}

const User = props => {
  React.useEffect(() => {
    if (_.isEmpty(props.profile)) {
      props.history.push('/login?returnTo=/open')
    }
  }, [])

  React.useEffect(() => {
    if (_.isEmpty(_.get('apolloUser.memberships', props.profile))) {
      props.history.push('/open')
    }
  }, [props.profile])

  const ticketsQueryResponse = useQuery(ticketsQuery)
  const ticketsGrouped = _.get('data.ticketsGrouped', ticketsQueryResponse)
  // TODO: insert closed for the winter component
  return (
    <div style={{ maxWidth: 1280, margin: '0 auto', padding: 24, paddingBottom: 64 }}>
      <BuilderComponent
        model='content-sections'
        entry={builders.openHeader}
      />
      <BuilderComponent
        model='content-sections'
        entry='b5530850c4e4467d98738094048e195a'
      />
      {/*       <BuilderComponent
        model='content-sections'
        entry={builders.openHeaderTickets}
      />
      <TicketRow ticketsGrouped={ticketsGrouped} />
      <LinkSection /> */}

      {/* <BuilderComponent
        model='content-sections'
        entry={builders.lastWeek}
      /> */}

      <BuilderComponent
        model='content-sections'
        entry={builders.openPartners1}
      />
      {/*       <BuilderComponent
        model='content-sections'
        entry={builders.openWeeklyPrices}
      /> */}
      <BuilderComponent
        model='content-sections'
        entry={builders.openPartners2}
      />
      <BuilderComponent
        model='content-sections'
        entry={builders.openFooter}
      />
    </div>
  )
}

export default _.compose(
  connect(
    s => {
      return {
        profile: ProfileReducer.selectors.getProfile(s)
      }
    },
    d => {
      return {
        loadProfile: () => d(ProfileReducer.actions.loadRequest(Date.now()))
      }
    }
  )
)(User)
